import ErrorBoundary from '@/pages/Errors/errorBoundary.tsx';
import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, RouteObject, RouterProvider } from 'react-router-dom';
import Loading from '@/components/ui/loading.tsx';
import ProtectedRoutes from '@/routes/protected-routes.tsx';
import { useTenantNavigation } from '@/utils/urlUtils.ts';

const PageLayout = lazy(() => import('@/components/ui/PageLayout.tsx'));
const AccessRestricted = lazy(() => import('@/pages/Errors/accessRestricted.tsx'));
const NotFound = lazy(() => import('@/pages/Errors/notFound.tsx'));
const UnexpectedError = lazy(() => import('@/pages/Errors/unexpectedError.tsx'));
const CustomError = lazy(() => import('@/pages/Layout/customErrorPage.tsx'));
const Valuations = lazy(() => import('@/pages/Valuations/valuations.tsx'));
const Rules = lazy(() => import('@/pages/Rules/rules.tsx'));
const Variables = lazy(() => import('@/pages/Variables/variables.tsx'));
const EditVariable = lazy(() => import('@/pages/Variables/edit-variable.tsx'));
const Settings = lazy(() => import('@/pages/Settings/Settings.tsx'));
const NewTemplatePage = lazy(() => import('@/pages/Settings/tabs/EmailTemplate/Edicao/newTemplatePage.tsx'));
const EditReport = lazy(() => import('@/pages/Settings/tabs/Reports/Insercao/EditReport.tsx'));
const Domains = lazy(() => import('@/pages/Domains/domains.tsx'));
const Logs = lazy(() => import('@/pages/Log/logs.tsx'));
const ConfigurationLogsDetails = lazy(() => import('@/pages/Log/Details/configuration-logs-detail.tsx'));
const ValuationLogsDetails = lazy(() => import('@/pages/Log/Details/valuation-logs-detail.tsx'));
const ValuationEdit = lazy(() => import('@/pages/Valuations/valuation-edit.tsx'));
const ValuationImport = lazy(() => import('@/pages/ValuationImport/valuation-import.tsx'));
const ValuationReports = lazy(() => import('@/pages/ValuationReports/valuation-reports.tsx'));
const ValuationErrors = lazy(() => import('@/pages/ValuationErrors/valuation-errors.tsx'));
const RuleErrors = lazy(() => import('@/pages/ValuationErrors/valuation-errors.tsx'));
function AppRoutes() {
  const { tenant } = useTenantNavigation();

const protectedRoutes: RouteObject[] = [
  {
    path: 'valuations',
    element: <Valuations/>,
  },
  {
    path: 'valuations/:id',
    element: <ValuationEdit/>,
  },
  {
    path: 'valuations/:id/import',
    element: <ValuationImport/>,
  },
  {
    path: 'valuations/:id/reports',
    element: <ValuationReports/>,
  },
  {
    path: 'valuations/:id/errors',
    element: <ValuationErrors/>,
  },
  {
    path: 'valuations/:id/errors/:ruleId',
    element: <RuleErrors/>,
  },
  {
    path: 'rules',
    element: <Rules/>,
  },
  {
    path: 'variables',
    element: <Variables/>,
  },
  {
    path: 'variables/:id',
    element: <EditVariable/>,
  },
  {
    path: 'settings',
    element: <Settings/>,
  },
  {
    path: 'settings/email-templates',
    element: <Settings/>,
  },
  {
    path: 'settings/email-templates/:id',
    element: <NewTemplatePage/>,
  },
  {
    path: 'settings/plan-groups',
    element: <Settings/>,
  },
  {
    path: 'settings/identification-configuration',
    element: <Settings/>,
  },
  {
    path: 'settings/reports',
    element: <Settings/>,
  },
  {
    path: 'settings/reports/:id',
    element: <EditReport/>,
  },
  {
    path: 'settings/reports/:id/:newId',
    element: <EditReport/>,
  },
  {
    path: 'domains',
    element: <Domains/>,
  },
  {
    path: 'logs',
    element: <Logs />,
  },
  {
    path: 'logs/valuation-logs',
    element: <Logs />,
  },
  {
    path: 'logs/configuration-logs',
    element: <Logs />,
  },
  {
    path: 'logs/valuation-logs/:id',
    element: <ValuationLogsDetails/>,
  },
  {
    path: 'logs/configuration-logs/:entityType/:logId',
    element: <ConfigurationLogsDetails />,
  },
];

const routes: RouteObject[] = [
  {
    path: 'error',
    element: <UnexpectedError/>
  },
  {
    path: 'error/redirect',
    element: <CustomError />
  },
  {
    path: 'error/access-restricted',
    element: <AccessRestricted/>,
  },
  {
    path: 'error/not-found',
    element: <NotFound/>
  },
  {
    path: 'signout',
    element: <Navigate to={'/' + (tenant || '')} replace/>
  },
  {
    path: ':tenantName',
    element: <PageLayout/>,
    children: [
      {
        path: '',
        element: (
          <ErrorBoundary>
            <Suspense fallback={<Loading />}>
              <ProtectedRoutes routes={protectedRoutes}/>
            </Suspense>
          </ErrorBoundary>
        ),
        children: protectedRoutes,
      },
    ]
  },
  {
    path: '*',
    element: <AccessRestricted/>,
  }
];

  const router = createBrowserRouter(routes);

  return <Suspense fallback={<Loading/>}>
    <RouterProvider router={router}/>
  </Suspense>;
}

export default AppRoutes;
